import React from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  Nav,
  NavLink,
  NavItem,
  TabContent,
  TabPane,
  Col,
  Row,
  Spinner,
} from 'reactstrap';
import IncotermTableFragment from './IncotermTableFragment';
import useQuantityDeliveryTabs from './hooks/useQuantityDeliveryTabs';
import { displayValue } from './utils/quantityDeliveryUtils';

const QuantityColumn = ({
  parcel,
  isLpg = false,
  quantityUnitLabel = isLpg ? 'MT' : 'MB',
}) => {
  const quantities = parcel.quantities ?? [];

  return (
    <Col md={4}>
      <div className='text-muted small fw-semibold mb-2'>Quantity</div>
      <div className='border rounded shadow-none bg-white'>
        <div className='bg-light p-2 border-bottom text-muted small fw-medium fs-12'>
          {quantityUnitLabel}
        </div>

        {quantities.length > 0 ? (
          quantities.map((qty, index) => (
            <div
              key={qty.id}
              className={`p-2 d-flex justify-content-between align-items-center ${
                index !== quantities.length - 1 ? 'border-bottom' : ''
              }`}
            >
              <span className='text-dark fs-13'>
                {displayValue(qty.quantity)}
              </span>
            </div>
          ))
        ) : (
          <div className='text-center text-muted p-3 fs-13'>
            No quantity data available
          </div>
        )}
      </div>
    </Col>
  );
};

const QuantityDeliverySection = ({
  parcels = [],
  isLoading = false,
  error = null,
  isLpg = false,
  quantityUnitLabel = isLpg ? 'MT' : 'MB',
  loadingMessage = 'Loading quantity & delivery data...',
  emptyMessage = 'No quantity delivery data available.',
}) => {
  const { parcelTabs, toggleParcelTab } = useQuantityDeliveryTabs(parcels);

  if (isLoading) {
    return (
      <div className='text-center py-4'>
        <Spinner size='sm' color='primary' />
        <p className='text-muted small mt-2 mb-0'>{loadingMessage}</p>
      </div>
    );
  }

  if (error) {
    return <div className='alert alert-danger mb-0'>{error}</div>;
  }

  if (!parcels.length) {
    return <p className='text-muted small mb-0'>{emptyMessage}</p>;
  }

  return parcels.map(parcel => {
    const activeTab = parcelTabs[parcel.id] || 'quantity';

    return (
      <Card key={parcel.id} className='border shadow-sm mb-4'>
        <CardHeader className='bg-white p-0 border-bottom'>
          <Nav tabs className='nav-tabs m-0'>
            <NavItem>
              <NavLink
                className={
                  activeTab === 'quantity'
                    ? 'active text-primary fw-semibold text-uppercase py-3 px-4'
                    : 'text-uppercase fw-semibold text-muted py-3 px-4'
                }
                onClick={() => toggleParcelTab(parcel.id, 'quantity')}
                style={{ cursor: 'pointer' }}
              >
                Quantity Delivery Time
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={
                  activeTab === 'pricing'
                    ? 'active text-primary fw-semibold text-uppercase py-3 px-4'
                    : 'text-uppercase fw-semibold text-muted py-3 px-4'
                }
                onClick={() => toggleParcelTab(parcel.id, 'pricing')}
                style={{ cursor: 'pointer' }}
              >
                Pricing
              </NavLink>
            </NavItem>
          </Nav>
        </CardHeader>

        <CardBody className='p-4'>
          <TabContent activeTab={activeTab}>
            <TabPane tabId='quantity'>
              <Row className='g-3 mb-4'>
                <Col md={6}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-1'>
                      Delivery Unit
                    </div>
                    <div className='text-dark fs-14 fw-medium'>
                      {displayValue(parcel.deliveryUnit)}
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-1'>
                      {isLpg ? 'Cargo Type' : 'Frequency'}
                    </div>
                    <div className='text-dark fs-14 fw-medium'>
                      {isLpg
                        ? displayValue(parcel.cargoTypeLabel)
                        : displayValue(parcel.frequency)}
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className='g-3'>
                <QuantityColumn
                  parcel={parcel}
                  isLpg={isLpg}
                  quantityUnitLabel={quantityUnitLabel}
                />
                <IncotermTableFragment items={parcel.incotermTimelineItems} />
              </Row>
            </TabPane>

            <TabPane tabId='pricing'>
              <Row className='g-3 mb-4'>
                <Col md={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-1'>
                      Delivery Unit
                    </div>
                    <div className='text-dark fs-14 fw-medium'>
                      {displayValue(parcel.deliveryUnit)}
                    </div>
                  </div>
                </Col>
                <Col md={8}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-1'>
                      Pricing Period
                    </div>
                    <div className='text-dark fs-14 fw-medium'>
                      {displayValue(parcel.pricingPeriodLabel)}
                    </div>
                  </div>
                </Col>
              </Row>

              <Row className='g-3 mb-4'>
                <Col md={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-1'>
                      Index Price Preference
                    </div>
                    <div className='text-dark fs-14 fw-medium'>
                      {displayValue(parcel.indexPrice)}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-1'>
                      Other Index Price
                    </div>
                    <div className='text-dark fs-14 fw-medium'>
                      {displayValue(parcel.otherIndexPrice)}
                    </div>
                  </div>
                </Col>
                <Col md={4}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-1'>
                      {parcel.pricingPeriodDetailLabel || 'Pricing Detail'}
                    </div>
                    <div className='text-dark fs-14 fw-medium'>
                      {displayValue(parcel.pricingPeriodDetail)}
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xs={12}>
                  <div className='border-start border-2 border-primary ps-3'>
                    <div className='text-muted small fw-semibold mb-2'>
                      Notes for Pricing
                    </div>
                    {parcel.notesForPricing ? (
                      <div className='html-content-panel'>
                        <div
                          className='html-content-body'
                          dangerouslySetInnerHTML={{
                            __html: parcel.notesForPricing,
                          }}
                        />
                      </div>
                    ) : (
                      <p className='text-dark fs-13 mb-0 lh-base'>-</p>
                    )}
                  </div>
                </Col>
              </Row>
            </TabPane>
          </TabContent>
        </CardBody>
      </Card>
    );
  });
};

export default QuantityDeliverySection;
